/**
 * Closing letter generation (Lloyd, Aug 21 2026).
 *
 * > *"The closing letter is really a structured list of court conditions and deadlines… I'll have it
 * > generate when a case closes, with the review request in the covering email, and produce a draft
 * > for approval before anything is sent."*
 *
 * Reading the conditions also returns the letter's readiness, so staff see what is missing BEFORE
 * they press a button, and only `generate` produces anything.
 *
 * **Writing conditions belongs to `CaseConditionsApi` / `CaseConditionsPanel`, not here.** Those
 * routes live under `/ticket/:guid/conditions/:conditionGuid`; this module only reads them.
 *
 * **It returns a PDF and the covering text. It does not send them.** That is the standing rule for
 * anything reaching a client, and doubly so for the letter that tells them they no longer have an
 * attorney.
 */

export type ConditionType =
  | 'DSC_COURSE'
  | 'COURSE_CERTIFICATE'
  | 'DRIVING_RECORD'
  | 'PAYMENT'
  | 'COMMUNITY_SERVICE'
  | 'OTHER';

export const CONDITION_TYPE_OPTIONS: Array<{
  value: ConditionType;
  label: string;
  /** Which extra field this type needs before the letter will generate. */
  requires: 'amount' | 'hours' | 'detail' | 'none';
}> = [
  { value: 'DSC_COURSE', label: 'Driving safety course', requires: 'none' },
  { value: 'COURSE_CERTIFICATE', label: 'Course completion certificate', requires: 'none' },
  { value: 'DRIVING_RECORD', label: 'Certified driving record', requires: 'none' },
  { value: 'PAYMENT', label: 'Payment to the court', requires: 'amount' },
  { value: 'COMMUNITY_SERVICE', label: 'Community service', requires: 'hours' },
  { value: 'OTHER', label: 'Other — describe it', requires: 'detail' },
];

export interface CaseConditionDto {
  CaseCondition_guid: string;
  CaseCondition_Type: string;
  CaseCondition_DueAt: string | null;
  CaseCondition_AmountCents: number | null;
  CaseCondition_Hours: number | null;
  CaseCondition_Detail: string | null;
  CaseCondition_CompletedAt: string | null;
  CaseCondition_RecordedBy: string | null;
}

export interface ClosingLetterBlockerDto {
  /** The condition's position in the list as staff see it, or null for a case-level problem. */
  index: number | null;
  reason: string;
}

export interface ConditionsResponseDto {
  conditions: CaseConditionDto[];
  readiness: { canGenerate: boolean; blockers: ClosingLetterBlockerDto[] };
  /** Bonds the firm is still personally on. A non-empty list is itself one of the blockers. */
  liveSuretyBonds: Array<{ guid?: string; type: string }>;
}

export interface GenerateClosingLetterInput {
  dispositionDate: string;
  dispositionDescription: string;
  representationEndedAt?: string | null;
  courtDeliveryAddress?: string | null;
}

export interface ClosingLetterResultDto {
  fileName: string;
  pdfBase64: string;
  email: { subject: string; html: string };
}

/** Thrown when the case cannot support an honest letter. Carries the reasons so the caller can list them. */
export class ClosingLetterBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: ClosingLetterBlockerDto[],
  ) {
    super(message);
    this.name = 'ClosingLetterBlockedError';
  }
}

export default class ClosingLetterApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async conditions(ticketGuid: string): Promise<ConditionsResponseDto> {
    const response = await fetch(
      `${ClosingLetterApi.host()}/api/v1/manager/ticket/${ticketGuid}/conditions`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load the conditions on this case');
    return response.json();
  }

  static async generate(
    ticketGuid: string,
    input: GenerateClosingLetterInput,
  ): Promise<ClosingLetterResultDto> {
    const response = await fetch(
      `${ClosingLetterApi.host()}/api/v1/manager/ticket/${ticketGuid}/closing-letter`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      throw new ClosingLetterBlockedError(
        err.message || 'This case is not ready for a closing letter.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.details || err.error || err.message || 'Failed to generate the closing letter',
      );
    }
    return response.json();
  }
}
